// FILE: /src/Components/Dashboard/UsageTracker.js (FULLY UPDATED & CORRECTED)

import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tooltip, Button } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { fetchBillingDetails } from '../../services/user.profile';
import { formatBytes } from '../../utils/formatters';

const BYTES_PER_GB = 1024 * 1024 * 1024;
const REFRESH_INTERVAL_MS = 30000;

const toNonNegativeNumber = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue >= 0
        ? numericValue
        : 0;
};

const CREDIT_BREAKDOWN_ITEMS = [
    {
        key: 'monthlyPlan',
        label: 'Monthly plan',
    },
    {
        key: 'promo',
        label: 'Promo credits',
    },
    {
        key: 'paidAddons',
        label: 'Paid add-ons',
    },
    {
        key: 'migration',
        label: 'Migrated credits',
    },
    {
        key: 'admin',
        label: 'Admin credits',
    },
];

const CreditBreakdownTooltip = ({ credits = {} }) => {
    const breakdown = credits.breakdown || {};

    const rows = CREDIT_BREAKDOWN_ITEMS.map((item) => {
        const values = breakdown[item.key] || {};

        return {
            ...item,
            total: toNonNegativeNumber(values.totalCredits),
            consumed: toNonNegativeNumber(values.consumedCredits),
            remaining: toNonNegativeNumber(values.remainingCredits),
            reserved: toNonNegativeNumber(values.reservedCredits),
        };
    }).filter((item) => {
        return (
            item.total > 0 ||
            item.consumed > 0 ||
            item.remaining > 0 ||
            item.reserved > 0
        );
    });

    return (
        <div className="credit-breakdown-tooltip-content py-1">
            <p className="mb-2 text-xs font-semibold text-white">
                Credit breakdown
            </p>

            <div className="space-y-2">
                {rows.length > 0 ? (
                    rows.map((item) => (
                        <div
                            key={item.key}
                            className="border-b border-white/10 pb-2 last:border-b-0 last:pb-0"
                        >
                            <div className="flex items-center justify-between gap-4">
                                <span className="text-xs text-white/75">
                                    {item.label}
                                </span>
                                <span className="text-xs font-semibold text-white">
                                    {item.consumed.toLocaleString()} /{' '}
                                    {item.total.toLocaleString()}
                                </span>
                            </div>
                            <div className="mt-1 flex items-center justify-between gap-4 text-[11px] text-white/55">
                                <span>
                                    {item.remaining.toLocaleString()} remaining
                                </span>
                                {item.reserved > 0 && (
                                    <span>
                                        {item.reserved.toLocaleString()} reserved
                                    </span>
                                )}
                            </div>
                        </div>
                    ))
                ) : (
                    <p className="mb-0 text-xs text-white/60">
                        No credit grants are currently available.
                    </p>
                )}
            </div>

            <div className="mt-3 border-t border-white/15 pt-2">
                <div className="flex items-center justify-between gap-4 text-xs">
                    <span className="text-white/65">
                        Total available
                    </span>
                    <span className="font-semibold text-white">
                        {toNonNegativeNumber(
                            credits.availableCredits ??
                            credits.remainingCredits
                        ).toLocaleString()}
                    </span>
                </div>
            </div>
        </div>
    );
};

const StorageBreakdownTooltip = ({ storage = {} }) => {
const brands = Array.isArray(storage.brands)
? storage.brands
: [];

return (
<div className="credit-breakdown-tooltip-content py-1">
<p className="mb-1 text-xs font-semibold text-white">
Shared account storage
</p>
<p className="mb-3 max-w-[280px] text-[11px] leading-5 text-white/60">
One storage pool is shared across all brands. Deleting assets or a brand returns that storage to the account.
</p>

<div className="space-y-2">
{brands.length > 0 ? (
brands.map((brand) => (
<div
key={brand.groupId}
className="border-b border-white/10 pb-2 last:border-b-0 last:pb-0"
>
<div className="flex items-center justify-between gap-4">
<span className="max-w-[160px] truncate text-xs text-white/75">
{brand.brandName}
</span>
<span className="text-xs font-semibold text-white">
{formatBytes(
toNonNegativeNumber(
brand.usedBytes
)
)}
</span>
</div>
<div className="mt-1 text-[11px] text-white/50">
{toNonNegativeNumber(
brand.assetCount
).toLocaleString()}{" "}
asset
{toNonNegativeNumber(
brand.assetCount
) === 1
? ""
: "s"}
</div>
</div>
))
) : (
<p className="mb-0 text-xs text-white/60">
No stored brand assets.
</p>
)}
</div>

<div className="mt-3 space-y-1.5 border-t border-white/15 pt-2 text-xs">
<div className="flex items-center justify-between gap-4">
<span className="text-white/65">
Used
</span>
<span className="font-semibold text-white">
{formatBytes(
toNonNegativeNumber(
storage.usedBytes
)
)}
</span>
</div>
<div className="flex items-center justify-between gap-4">
<span className="text-white/65">
Available
</span>
<span className="font-semibold text-white">
{formatBytes(
toNonNegativeNumber(
storage.remainingBytes
)
)}
</span>
</div>
<div className="flex items-center justify-between gap-4">
<span className="text-white/65">
Total
</span>
<span className="font-semibold text-white">
{formatBytes(
toNonNegativeNumber(
storage.totalBytes
)
)}
</span>
</div>
</div>
</div>
);
};

const UsageBar = ({
label,
used,
limit,
isStorage = false,
infoContent = null,
}) => {
    const percentage = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
    const isExceeded = limit > 0 && used > limit;

    const usedFormatted = isStorage ? formatBytes(used) : used.toLocaleString();
    const limitFormatted = limit > 0
        ? isStorage
            ? formatBytes(limit)
            : limit.toLocaleString()
        : 'Unlimited';

    return (
        <div className="text-xs">
            <Tooltip
                title={
                    infoContent
                        ? null
                        : `${label}: ${usedFormatted} / ${limitFormatted}`
                }
            >
                <div>
                    <div className="mb-1 flex min-w-0 items-start justify-between gap-2 font-semibold">
                        <span className="flex min-w-0 flex-1 items-start gap-1.5 text-sidebar-text">
                            <span className="min-w-0 break-words">
                                {label}
                            </span>
                            {infoContent && (
                                <Tooltip
                                    title={infoContent}
                                    placement="right"
                                    mouseEnterDelay={0.15}
                                    overlayClassName="credit-breakdown-tooltip"
                                >
                                    <button
                                        type="button"
                                        className="inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-sidebar-text/60 transition hover:bg-sidebar-hover-bg hover:text-sidebar-active-text"
                                        aria-label={`${label} breakdown`}
                                    >
                                        <InfoCircleOutlined />
                                    </button>
                                </Tooltip>
                            )}
                        </span>
                        <span className={`flex-shrink-0 whitespace-nowrap ${
                            isExceeded
                                ? 'text-red-400'
                                : 'text-sidebar-active-text'
                        }`}>
                            {usedFormatted} / {limitFormatted}
                        </span>
                    </div>
                    <div className="w-full bg-sidebar-active-bg rounded-full h-1.5">
                        <div
                            className={`h-1.5 rounded-full transition-all duration-500 ${
                                isExceeded ? 'bg-red-500' : 'bg-brand-progress'
                            }`}
                            style={{ width: `${percentage}%` }}
                        ></div>
                    </div>
                </div>
            </Tooltip>
        </div>
    );
};

export default function UsageTracker() {
    const navigate = useNavigate();
    const location = useLocation();
    const { totalSize } = useSelector((state) => state.library);
    const [billingDetails, setBillingDetails] = useState(null);

    const refreshUsage = useCallback(async () => {
        try {
            const response = await fetchBillingDetails();
            setBillingDetails(response);
        } catch (error) {
            console.error('Failed to refresh sidebar usage:', error);
        }
    }, []);

    useEffect(() => {
        refreshUsage();
    }, [location.pathname, refreshUsage]);

    useEffect(() => {
        const handleWindowFocus = () => {
            refreshUsage();
        };

        const intervalId = window.setInterval(
            refreshUsage,
            REFRESH_INTERVAL_MS
        );

        window.addEventListener('focus', handleWindowFocus);

        return () => {
            window.clearInterval(intervalId);
            window.removeEventListener('focus', handleWindowFocus);
        };
    }, [refreshUsage]);

    const handleBuyMore = () => {
        navigate('/Dashboard/AccountSetting');
    };

    if (!billingDetails) {
        return null;
    }

    const aiConsumed = toNonNegativeNumber(
        billingDetails.credits?.consumedCredits ??
        billingDetails.usage?.aiCreditsConsumed
    );
    const totalAiLimit = toNonNegativeNumber(
        billingDetails.credits?.totalCredits ??
        billingDetails.limits?.aiCreditsTotal
    );

    const serverStorageUsedBytes =
        toNonNegativeNumber(
            billingDetails.storage?.usedBytes ??
            billingDetails.usage?.storageBytes ??
            (
                toNonNegativeNumber(
                    billingDetails.usage?.storageGB
                ) * BYTES_PER_GB
            )
        );
    const libraryStorageUsedBytes = toNonNegativeNumber(totalSize);
    const storageUsedBytes =
        billingDetails.storage?.usedBytes !== undefined
            ? serverStorageUsedBytes
            : Math.max(
                  serverStorageUsedBytes,
                  libraryStorageUsedBytes
              );
    const totalStorageLimitBytes =
        toNonNegativeNumber(
            billingDetails.storage?.totalBytes ??
            billingDetails.limits?.storageBytes ??
            (
                toNonNegativeNumber(
                    billingDetails.limits?.storageGB
                ) * BYTES_PER_GB
            )
        );

    return (
        <div className="p-4 mx-2 mb-2 border-t border-sidebar-border space-y-4">
            <UsageBar
                label="AI Credits Consumed"
                used={aiConsumed}
                limit={totalAiLimit}
                infoContent={
                    <CreditBreakdownTooltip
                        credits={billingDetails.credits}
                    />
                }
            />
            <UsageBar
                label="Shared Storage"
                used={storageUsedBytes}
                limit={totalStorageLimitBytes}
                isStorage
                infoContent={
                    <StorageBreakdownTooltip
                        storage={billingDetails.storage}
                    />
                }
            />
            <Button
                type="primary"
                block
                onClick={handleBuyMore}
                className="bg-brand-primary hover:bg-brand-primary-hover border-none mt-2"
            >
                Buy Add-ons
            </Button>
        </div>
    );
}